/**
 * Shared utilities for endpoint controls
 */

/** Background color shared by the 2D graph canvas and the 3D performance scene */
export const GRAPH_BG = '#0f172a';

/** Accent color for constraint nodes and their connections */
export const CONSTRAINT_COLOR = '#14b8a6';

// Colors by data type and data flow
export const TYPE_COLORS: Record<string, string> = {
  // Data endpoints - blue
  number: '#3b82f6',
  pattern: '#3b82f6',
  string: '#3b82f6',
  array: '#3b82f6',
  boolean: '#3b82f6',
  envelope: '#3b82f6',
  color: '#3b82f6',
  // Events/triggers (dataFlow) - green
  trigger: '#22c55e',
  event: '#22c55e',
  // Audio - orange
  audio: '#f97316',
  // Video/Hydra/Nodebox - purple (interoperable)
  video: '#a855f7',
  hydra: '#a855f7',
  nodebox: '#a855f7',
  three: 'yellow',
  constraint: '#6b7280',
  // Fallback
  any: '#6b7280',
};

/**
 * Extract adopted getter expression from store payload.
 * Returns the expression source text if an upstream connection provides a getter
 * (and the endpoint doesn't have its own useExp), or null otherwise.
 */
export function extractAdoptedGet(useExp: boolean | undefined, payloadGet: any): string | null {
  if (useExp || !payloadGet) return null;
  return String(payloadGet);
}

/**
 * Check if source and target dataTypes are compatible for connection.
 * Supports union types (e.g., 'array|polylines').
 */
export function areTypesCompatible(sourceType: string, targetTypes: string | readonly string[]): boolean {
  if (sourceType === 'any') return true;
  const source = sourceType.split('|');
  const target: readonly string[] = typeof targetTypes === 'string' ? targetTypes.split('|') : targetTypes;
  return target.includes('any') || source.some(s => target.includes(s));
}

/**
 * Get endpoint color based on dataFlow (for trigger/event) or dataType.
 * For union types like 'array|polylines', uses the first type's color.
 */
export function getEndpointColor(dataType?: string, dataFlow?: string): string {
  // Check dataFlow first for trigger/event coloring
  if (dataFlow === 'trigger' || dataFlow === 'event') {
    return TYPE_COLORS[dataFlow];
  }
  // For union types, use first type's color
  const primary = dataType?.split('|')[0] ?? 'any';
  return TYPE_COLORS[primary] ?? TYPE_COLORS.any;
}

/**
 * Format value for display
 */
export function formatValue(value: any): string {
  if (value === undefined || value === null) return '—';

  // Extract value from event objects (skip time property)
  if (typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
    return formatValue(value.value);
  }

  // Format envelope data compactly
  if (typeof value === 'object' && !Array.isArray(value) &&
      'attack' in value && 'decay' in value && 'sustain' in value && 'release' in value) {
    const { attack, decay, sustain, release } = value;
    return `A${attack.toFixed(2)} D${decay.toFixed(2)} S${sustain.toFixed(2)} R${release.toFixed(2)}`;
  }

  if (Array.isArray(value)) {
    if (value.length > 4) {
      return `[${value.slice(0, 3).join(', ')}… +${value.length - 3}]`;
    }
    return `[${value.join(', ')}]`;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Get category priority for endpoint sorting.
 * Order: preset (0) -> event/trigger (1) -> audio/video (2) -> data (3)
 * Checks dataFlow for trigger/event, dataType for audio/video.
 */
function getEndpointPriority(dataType?: string, dataFlow?: string): number {
  if (dataFlow === 'trigger') return 1;
  if (dataFlow === 'event') return 2;
  if (dataType === 'audio' || dataType === 'video' || dataType === 'hydra' || dataType === 'nodebox') return 3;
  return 4; // data types: number, string, boolean, array, pattern, envelope
}

/**
 * Sort endpoints in order: preset, events/triggers, audio/video, then data endpoints.
 * Data endpoints are grouped by their `group` property.
 * Within each category/group, use `order` if supplied, otherwise alphabetical.
 */
export function sortEndpoints<T extends { dataType?: string; dataFlow?: string; group?: string; order?: number }>(
  endpoints: [string, T][]
): [string, T][] {
  return endpoints.sort(([aName, a], [bName, b]) => {
    // Preset always comes first
    if (aName === 'preset') return -1;
    if (bName === 'preset') return 1;

    const aPriority = getEndpointPriority(a.dataType, a.dataFlow);
    const bPriority = getEndpointPriority(b.dataType, b.dataFlow);

    // Sort by type category first
    if (aPriority !== bPriority) return aPriority - bPriority;

    // Within data endpoints, group by the group property
    if (aPriority === 4) {
      const aGroup = a.group;
      const bGroup = b.group;
      // Ungrouped endpoints come before grouped ones
      if (aGroup && !bGroup) return 1;
      if (bGroup && !aGroup) return -1;
      // Sort groups alphabetically
      if (aGroup && bGroup && aGroup !== bGroup) return aGroup.localeCompare(bGroup);
    }

    // Within same category/group, use order if supplied
    const aOrder = a.order;
    const bOrder = b.order;
    if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder;
    if (aOrder !== undefined) return -1; // ordered items come before unordered
    if (bOrder !== undefined) return 1;

    // Fall back to alphabetical
    return aName.localeCompare(bName);
  });
}

/**
 * Item in the grouped endpoint list - either a group header or an endpoint
 */
export type GroupedEndpointItem<T> =
  | { kind: 'endpoint'; name: string; endpoint: T }
  | { kind: 'group'; name: string };

/**
 * Group sorted endpoints by their group property.
 * Returns a flat list with group headers inserted before their endpoints.
 */
export function groupEndpoints<T extends { group?: string }>(
  sortedEndpoints: [string, T][]
): GroupedEndpointItem<T>[] {
  const result: GroupedEndpointItem<T>[] = [];
  let currentGroup: string | undefined = undefined;

  for (const [name, endpoint] of sortedEndpoints) {
    const group = endpoint.group;

    // Insert group header when entering a new group
    if (group && group !== currentGroup) {
      result.push({ kind: 'group', name: group });
      currentGroup = group;
    } else if (!group && currentGroup) {
      // Left grouped section (shouldn't happen with proper sorting, but handle it)
      currentGroup = undefined;
    }

    result.push({ kind: 'endpoint', name, endpoint });
  }

  return result;
}
